import { WarehouseItemWorkRequestRejectionReason } from "@redotech/cockroachdb-codegen/tables/fulfillment/warehouse-item-work-request-rejection-reason";
import { inventoryItemIdFormat } from "@redotech/redo-inventory-id";
import {
  photoInstructionStepIdFormat,
  warehouseItemPhotoCaptureIdFormat,
  warehouseItemWorkRequestIdFormat,
} from "@redotech/redo-warehouse-id";
import * as z from "zod";
import {
  ItemPhotoCaptureSchema,
  ItemPhotoRequestDetailSchema,
} from "./item-work-request-types";

/**
 * What a scanned inventory item turned out to be. `rejected` is a request the
 * server just closed as impossible to perform, so the operator is told why.
 */
export const ItemPhotoRequestScanResultSchema = z.discriminatedUnion(
  "outcome",
  [
    z.object({ outcome: z.literal("none") }),
    z.object({
      outcome: z.literal("ready"),
      request: ItemPhotoRequestDetailSchema,
    }),
    z.object({
      outcome: z.literal("rejected"),
      reason: z.enum(WarehouseItemWorkRequestRejectionReason),
    }),
  ],
);

export type ItemPhotoRequestScanResult = z.infer<
  typeof ItemPhotoRequestScanResultSchema
>;

/** Scan lookup: an inventory item id typed or scanned by the operator. */
export const getItemPhotoRequestForItem = {
  name: "getItemPhotoRequestForItem" as const,
  input: z.object({ inventoryItemId: inventoryItemIdFormat.schema }),
  output: z.object({ result: ItemPhotoRequestScanResultSchema }),
};

/**
 * Multipart upload of one captured frame, as `uploadParcelPhoto` does. The
 * first photo also claims the request, so scanning alone does not take it.
 */
export const uploadItemPhotoCapture = {
  name: "uploadItemPhotoCapture" as const,
  input: z.object({
    requestId: warehouseItemWorkRequestIdFormat.schema,
    /** The direction this photo answers; null for an extra, unprompted shot. */
    photoInstructionStepId: photoInstructionStepIdFormat.schema.nullish(),
  }),
  output: z.object({ photo: ItemPhotoCaptureSchema }),
};

/** Discards a shot the operator wants to retake. */
export const deleteItemPhotoCapture = {
  name: "deleteItemPhotoCapture" as const,
  input: z.object({
    requestId: warehouseItemWorkRequestIdFormat.schema,
    photoId: warehouseItemPhotoCaptureIdFormat.schema,
  }),
  output: z.null(),
};

export const completeItemWorkRequest = {
  name: "completeItemWorkRequest" as const,
  input: z.object({ requestId: warehouseItemWorkRequestIdFormat.schema }),
  output: z.object({ request: ItemPhotoRequestDetailSchema }),
};
